



import styled, { keyframes } from 'styled-components';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { ListTodo, X } from 'lucide-react';
import { useAuth } from '../../context/AuthProvider';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import { getSubscription } from '../../store/features/billing/billingSlice';
// import SubscriptionCard from '../../components/dashboard/SubscriptionCard';
import ActiveSubscriptionHero from '../../components/subscriptions/ActiveSubscriptionHero';
import type { PlanId } from '../../components/subscriptions/ActivePlanPreview';
import ClientScheduleOverview from '../../components/dashboard/ClientScheduleOverview';
import TeamMembersCard from '../../components/dashboard/TeamMembersCard';
import InviteTeammateModal from '../../components/modals/InviteTeammateModal';
import OnboardingTasksPanel from '../../components/onboarding/OnboardingTasksPanel';
import {
  fetchClientDashboardSummary,
  type ClientDashboardSummary,
} from '../../lib/clientDashboard';

 import TicketEditorDrawer, { EditorMode } from '../../components/tickets/components/TicketEditorDrawer';
 import { useApi } from '../../context/ApiProvider';
 import { buildTicketEditorTicket } from '../../lib/tickets/ticketEditorTicket';
 import {
   fetchTickets,
   selectCachedTicketById,
   assignTicket,
   updateTicketStatus,
   ticketUpserted,
 } from '../../store/features/tickets/ticketsSlice';


import Card from '../../components/Card';

const PageWrap = styled.div`
  min-height: calc(100vh - 6rem);
  display: grid;
  place-items: start center;
  padding: 1rem;
`;

const Grid = styled.div`
  width: 100%;
  max-width: 1280px;
  display: grid;
  gap: 1rem;

  /* 12-col bento on desktop, collapses responsively */
  grid-template-columns: repeat(12, 1fr);

  @media (max-width: 1200px) { grid-template-columns: repeat(8, 1fr); }
  @media (max-width: 900px)  { grid-template-columns: repeat(4, 1fr); }
`;

const Span8 = styled.div`
   grid-column: span 8;
   @media (max-width: 1200px) { grid-column: span 8; }
   @media (max-width: 900px)  { grid-column: span 4; }
 `;

const Span12 = styled.div`
  grid-column: 1 / -1;   /* span all columns at every breakpoint */
`;

const Span6Right = styled.div`
  grid-column: 7 / -1;
  @media (max-width: 1200px) { grid-column: 5 / -1; }
  @media (max-width: 900px)  { grid-column: span 4; }
`;

const ROW_PANEL_HEIGHT = 'clamp(240px, 28vh, 300px)';
const TOP_OVERVIEW_WELCOME_HEIGHT = '5.5rem';
const TOP_OVERVIEW_TOTAL_HEIGHT = `calc(${TOP_OVERVIEW_WELCOME_HEIGHT} + ${ROW_PANEL_HEIGHT} + 0.35rem)`;

const FixedHeightPanel = styled.div`
  height: ${ROW_PANEL_HEIGHT};
  min-height: 0;
`;

const TopOverviewGrid = styled.div`
  display: grid;
  column-gap: 1rem;
  row-gap: 0.35rem;
  grid-template-columns: minmax(0, 1.9fr) minmax(280px, 0.8fr);
  grid-template-rows: ${TOP_OVERVIEW_WELCOME_HEIGHT} ${ROW_PANEL_HEIGHT};
  grid-template-areas:
    'welcome tasks'
    'snapshot tasks';
  align-items: stretch;
  min-height: 0;
  height: ${TOP_OVERVIEW_TOTAL_HEIGHT};
  max-height: ${TOP_OVERVIEW_TOTAL_HEIGHT};
  overflow: hidden;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;
    grid-template-areas:
      'welcome'
      'snapshot'
      'tasks';
    height: auto;
    max-height: none;
    overflow: visible;
  }

  @media (max-width: 760px) {
    grid-template-rows: auto auto;
    grid-template-areas:
      'welcome'
      'snapshot';
  }
`;

const TopWelcomeBlock = styled.div`
  grid-area: welcome;
  min-width: 0;
  min-height: 0;
  height: 100%;
`;

const TopSnapshotBlock = styled(FixedHeightPanel)`
  grid-area: snapshot;
  min-width: 0;

  @media (max-width: 1200px) {
    height: auto;
  }
`;

const TopTasksBlock = styled.div`
  grid-area: tasks;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  align-self: stretch;
  display: grid;

  @media (max-width: 760px) {
    display: none;
  }
`;

const MobileTasksEdgeTab = styled.button`
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  min-height: 2.8rem;
  padding: 0 1.05rem;
  border: 1px solid rgba(224, 161, 94, 0.3);
  border-radius: 999px;
  background: rgba(18, 20, 25, 0.94);
  color: #f4f6fb;
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.34);
  display: none;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.8rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  z-index: 1330;

  @media (max-width: 760px) {
    display: inline-flex;
  }
`;

const MobileTasksOverlay = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: none;
  padding: 0;
  margin: 0;
  background: rgba(8, 10, 14, 0.58);
  backdrop-filter: blur(4px);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 240ms ease;
  z-index: 1335;
  display: none;

  @media (max-width: 760px) {
    display: block;
  }
`;

const MobileTasksDrawer = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: min(380px, calc(100vw - 0.9rem));
  height: 100vh;
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.98), rgba(18, 20, 25, 0.98));
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: -20px 0 48px rgba(0, 0, 0, 0.42);
  display: none;
  grid-template-rows: auto 1fr;
  min-height: 0;
  transform: translateX(${({ $open }) => ($open ? '0' : 'calc(100% + 1rem)')});
  transition: transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
  z-index: 1340;

  @media (max-width: 760px) {
    display: grid;
  }
`;

const MobileTasksDrawerHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: start;
  padding: 1rem 1rem 0.9rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const MobileTasksDrawerHeaderText = styled.div`
  display: grid;
  gap: 0.25rem;
  min-width: 0;
`;

const MobileTasksDrawerEyebrow = styled.div`
  color: rgba(224, 161, 94, 0.76);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const MobileTasksDrawerTitle = styled.div`
  color: #e0a15e;
  font-size: 1.2rem;
  font-weight: 900;
  line-height: 1.08;
`;

const MobileTasksDrawerSubtitle = styled.div`
  color: rgba(232, 234, 237, 0.74);
  font-size: 0.82rem;
  line-height: 1.5;
  font-weight: 700;
`;

const MobileTasksDrawerClose = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #f4f6fb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const MobileTasksDrawerBody = styled.div`
  min-height: 0;
  padding: 1rem;
  overflow: hidden;
  display: grid;
`;

const MobileTasksPanel = styled(OnboardingTasksPanel)`
  height: 100%;
  min-height: 0;
`;

const panelReveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SnapshotCard = styled(Card)`
  height: 100%;
  display: grid;
  padding: 1rem;
  animation: ${panelReveal} 420ms cubic-bezier(.2, .72, .24, 1) both;

  @media (max-width: 1200px) {
    height: auto;
  }

  @media (max-width: 560px) {
    padding: 0.9rem;
  }
`;

const SnapshotKicker = styled.div`
  color: rgba(232, 234, 237, 0.88);
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  text-align: center;
`;

const SnapshotBody = styled.div`
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 560px) {
    gap: 1rem;
  }
`;

const SnapshotPanel = styled.div`
  min-height: 0;
`;

const UtilizationPanel = styled(SnapshotPanel)`
  display: grid;
  justify-items: center;
  align-content: start;
  gap: 1rem;
  width: 100%;

  @media (max-width: 560px) {
    gap: 0.8rem;
  }
`;

const UtilizationBody = styled.div`
  display: grid;
  place-items: center;
`;

const UtilizationRingShell = styled.div<{ $gradient: string }>`
  width: 196px;
  height: 196px;
  border-radius: 999px;
  padding: 14px;
  display: grid;
  place-items: center;
  background: ${({ $gradient }) => $gradient};
  box-shadow:
    inset 0 0 0 1px rgba(255,255,255,.04),
    0 12px 28px rgba(0,0,0,.18);

  @media (max-width: 900px) {
    width: 176px;
    height: 176px;
    padding: 12px;
  }

  @media (max-width: 560px) {
    width: 152px;
    height: 152px;
    padding: 10px;
  }
`;

const UtilizationRingInner = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(22,24,30,.98), rgba(18,20,25,.98));
  display: grid;
  place-items: center;
  padding: 18px;
  text-align: center;
  gap: 6px;

  @media (max-width: 560px) {
    padding: 14px;
    gap: 4px;
  }
`;

const UtilizationRingValue = styled.div`
  color: #e8eaed;
  font-size: 30px;
  line-height: 1.02;
  font-weight: 900;

  @media (max-width: 900px) {
    font-size: 26px;
  }

  @media (max-width: 560px) {
    font-size: 22px;
  }
`;

const UtilizationRingMeta = styled.div`
  color: rgba(186,198,224,.82);
  font-size: 10px;
  font-weight: 800;
  line-height: 1.3;
  max-width: 96px;
  text-transform: uppercase;
  letter-spacing: .04em;

  @media (max-width: 560px) {
    font-size: 9px;
    max-width: 86px;
  }
`;

const SectionLabel = styled.div`
  color: rgba(180, 194, 214, 0.75);
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  text-align: center;
`;

const DeliverySummaryCard = styled(SnapshotPanel)`
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 0.9rem;
  justify-items: center;
  justify-self: center;
  width: 100%;
  height: 100%;
  text-align: center;

  @media (max-width: 560px) {
    gap: 0.8rem;
  }
`;

const ProjectRingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(148px, 1fr));
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.1rem;
  align-items: start;
  justify-items: center;
  align-content: center;
  align-self: center;
  width: min(100%, 360px);
  min-height: 360px;
  margin-inline: auto;
  justify-self: center;

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
    min-height: 0;
    gap: 0.8rem 0.7rem;
  }
`;

const ProjectRingSlot = styled.div<{ $empty?: boolean }>`
  display: grid;
  justify-items: center;
  align-content: start;
  gap: 0.65rem;
  min-width: 0;
  width: 100%;
  min-height: 168px;
  opacity: ${({ $empty }) => ($empty ? 0.28 : 1)};

  @media (max-width: 560px) {
    min-height: 142px;
    gap: 0.5rem;
  }
`;

const ProjectRingShell = styled.div<{ $gradient: string }>`
  width: min(100%, 132px);
  aspect-ratio: 1;
  border-radius: 999px;
  padding: 11px;
  display: grid;
  place-items: center;
  background: ${({ $gradient }) => $gradient};
  box-shadow:
    inset 0 0 0 1px rgba(255,255,255,.04),
    0 10px 22px rgba(0,0,0,.16);

  @media (max-width: 560px) {
    width: min(100%, 108px);
    padding: 9px;
  }
`;

const ProjectRingGhostShell = styled(ProjectRingShell)`
  background: linear-gradient(180deg, rgba(255,255,255,.05), rgba(255,255,255,.03));
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.04);
`;

const ProjectRingInner = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(22,24,30,.98), rgba(18,20,25,.98));
  display: grid;
  place-items: center;
  padding: 14px;
  text-align: center;
  gap: 4px;

  @media (max-width: 560px) {
    padding: 11px;
  }
`;

const ProjectRingGhostInner = styled(ProjectRingInner)`
  background: linear-gradient(180deg, rgba(22,24,30,.74), rgba(18,20,25,.68));
`;

const ProjectRingValue = styled.div`
  color: #f8fbff;
  font-size: 1.25rem;
  font-weight: 900;
  line-height: 1.02;

  @media (max-width: 560px) {
    font-size: 1.05rem;
  }
`;

const ProjectRingMeta = styled.div`
  color: rgba(186,198,224,.74);
  font-size: 0.56rem;
  font-weight: 900;
  line-height: 1.25;
  text-transform: uppercase;
  letter-spacing: 0.12em;

  @media (max-width: 560px) {
    font-size: 0.5rem;
  }
`;

const ProjectRingName = styled.div`
  width: min(100%, 160px);
  color: #f3f6fb;
  font-size: 0.8rem;
  font-weight: 800;
  line-height: 1.25;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 560px) {
    font-size: 0.72rem;
    width: 100%;
  }
`;

const ProjectRingEmpty = styled.div`
  color: rgba(206, 216, 231, 0.72);
  font-size: 0.8rem;
  line-height: 1.5;
  font-weight: 700;
  max-width: 28ch;
`;

const ProjectRingGhostName = styled(ProjectRingName)`
  color: rgba(186,198,224,.32);
`;

const TeamMembersFill = styled(TeamMembersCard)`
  height: 100%;
  min-height: 0;
`;

const Welcome = styled.div`
  display: grid;
  align-content: start;
  gap: .2rem;
  padding: .2rem 1.25rem 0;
  h2 { margin: 0; font-weight: 900; color: #e0a15e; font-size: clamp(3.25rem, 2.2vw, 1.6rem); }
  p  { margin: 0; color:rgb(194, 194, 194); }
`;

const skeletonPulse = keyframes`
  0% {
    opacity: 0.45;
  }
  50% {
    opacity: 0.92;
  }
  100% {
    opacity: 0.45;
  }
`;

const SkeletonBlock = styled.div<{
  $width?: string;
  $height?: string;
  $radius?: string;
}>`
  width: ${({ $width }) => $width ?? '100%'};
  height: ${({ $height }) => $height ?? '1rem'};
  border-radius: ${({ $radius }) => $radius ?? '12px'};
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05),
    rgba(255, 255, 255, 0.12),
    rgba(255, 255, 255, 0.05)
  );
  animation: ${skeletonPulse} 1.4s ease-in-out infinite;
`;

const DashboardSkeletonCard = styled(Card)`
  height: 100%;
  min-height: 0;
  padding: 1rem;
  display: grid;
  gap: 0.9rem;
  overflow: hidden;
`;

const WelcomeSkeleton = styled.div`
  display: grid;
  align-content: start;
  gap: 0.4rem;
  padding: 0.2rem 1.25rem 0;
`;

const SnapshotSkeletonLayout = styled.div`
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SnapshotSkeletonPanel = styled.div`
  display: grid;
  justify-items: center;
  align-content: start;
  gap: 1rem;
  min-height: 0;
`;

const SkeletonRing = styled(SkeletonBlock)`
  width: 196px;
  height: 196px;
  border-radius: 999px;

  @media (max-width: 900px) {
    width: 176px;
    height: 176px;
  }

  @media (max-width: 560px) {
    width: 152px;
    height: 152px;
  }
`;

const TasksSkeletonList = styled.div`
  display: grid;
  gap: 0.75rem;
  align-content: start;
  min-height: 0;
`;

const TaskSkeletonItem = styled.div`
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.03);
  padding: 0.85rem 0.9rem;
  display: grid;
  gap: 0.45rem;
`;

const HeroSkeletonCard = styled(Card)`
  min-height: 118px;
  padding: 1.15rem 1.25rem;
  display: grid;
  gap: 0.75rem;
`;

const ScheduleSkeletonGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1.65fr) minmax(300px, 0.95fr);

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const ScheduleSkeletonCalendar = styled(Card)`
  padding: 1.1rem;
  display: grid;
  gap: 1rem;
`;

const ScheduleSkeletonCalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.55rem;
`;

const ScheduleSkeletonDay = styled.div`
  min-height: 92px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
  padding: 0.65rem 0.55rem;
  display: grid;
  align-content: space-between;
  justify-items: center;
  gap: 0.45rem;
`;

const ScheduleSkeletonRail = styled.div`
  display: grid;
  gap: 1rem;
`;

const ScheduleSkeletonRailCard = styled(Card)`
  padding: 1.1rem;
  display: grid;
  gap: 0.85rem;
`;

const PLAN_LABELS: Record<PlanId, string> = {
  marketing: 'Marketing Services',
  'digital-services': 'Digital Services',
  'ecommerce-digital-services': 'Ecommerce + Digital',
};

function formatHours(value: number | null | undefined, fallback = '--'): string {
  if (!Number.isFinite(value) || value === null || value === undefined) return fallback;
  const hours = Math.max(0, value) / 60;
  if (hours >= 10) return `${Math.round(hours)}h`;
  const rounded = Math.round(hours * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}h`;
}

function formatWholeHours(value: number | null | undefined, fallback = '--'): string {
  if (!Number.isFinite(value) || value === null || value === undefined) return fallback;
  return `${Math.max(0, Math.round(Number(value) / 60))}h`;
}

export default function ClientHomePage() {
  const {
    isPaid,
    orgId,
    businessProfileComplete,
    isPrimaryAccountHolder,
    organizations,
  } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [dashboardSummary, setDashboardSummary] = useState<ClientDashboardSummary | null>(null);
  const [dashboardSummaryLoading, setDashboardSummaryLoading] = useState(true);
  const [isMobileTasksMode, setIsMobileTasksMode] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 760 : false,
  );
  const [mobileTasksOpen, setMobileTasksOpen] = useState(false);
  const openInvite = useCallback(() => setInviteOpen(true), []);
  const closeInvite = useCallback(() => setInviteOpen(false), []);
  const openMobileTasks = useCallback(() => setMobileTasksOpen(true), []);
  const closeMobileTasks = useCallback(() => setMobileTasksOpen(false), []);
  const dispatch = useDispatch<AppDispatch>();
  const { subscription } = useSelector((s: RootState) => s.billing);
  const api = useApi();

 // open the editor in "edit" mode for a ticket id
 const [editorOpen, setEditorOpen] = useState(false);
 const [editorMode] = useState<EditorMode>('edit');
 const [editingId, setEditingId] = useState<string | null>(null);

 // ticket from store for the current editingId
 const editing = useSelector((s: RootState) =>
   editingId ? selectCachedTicketById(editingId)(s) : undefined
 );

 // Load tickets (so the card has data to resolve by id)
 useEffect(() => {
   dispatch(fetchTickets({ orderBy: 'plannedEndAt', direction: 'asc', limit: 200 }));
 }, [dispatch]);

 useEffect(() => { dispatch(getSubscription()); }, [dispatch]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 760px)');
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsMobileTasksMode(event.matches);
    };

    handleChange(mediaQuery);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (!isMobileTasksMode) {
      setMobileTasksOpen(false);
    }
  }, [isMobileTasksMode]);

  useEffect(() => {
    if (!isMobileTasksMode || !mobileTasksOpen || typeof document === 'undefined') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileTasksMode, mobileTasksOpen]);

  useEffect(() => {
    let active = true;
    setDashboardSummaryLoading(true);
    (async () => {
      try {
        const response = await fetchClientDashboardSummary(orgId);
        if (active) {
          setDashboardSummary(response);
        }
      } catch (error) {
        console.warn('[ClientHomePage] Failed to load client dashboard summary', error);
        if (active) {
          setDashboardSummary(null);
        }
      } finally {
        if (active) {
          setDashboardSummaryLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [orgId]);

  const status = (subscription as any)?.status as string | undefined;
  const isSubActive = useMemo(
    () => Boolean(isPaid || (status && ['active', 'trialing'].includes(status))),
    [isPaid, status]
  );

  const activePlanId = useMemo<PlanId | null>(() => {
    const sub: any = subscription || {};
    return (sub?.planId as PlanId | undefined)
      ?? (sub?.metadata?.planId as PlanId | undefined)
      ?? null;
  }, [subscription]);

  const monthly = useMemo(() => {
    const sub: any = subscription || {};
    const price = sub?.items?.data?.[0]?.price ?? sub?.items?.[0]?.price;
    const cents = typeof price?.unit_amount === 'number'
      ? price.unit_amount
      : (typeof price?.unit_amount_decimal === 'string'
        ? Math.round(parseFloat(price.unit_amount_decimal))
        : null);
    return typeof cents === 'number' ? (cents / 100).toFixed(2) : null;
  }, [subscription]);

  const planLabel = activePlanId ? PLAN_LABELS[activePlanId] : 'Active subscription';

  const subText = useMemo(() => {
    if (!isSubActive) return null;
    const sub: any = subscription || {};
    const ts = status === 'trialing' ? sub?.trial_end : sub?.current_period_end;
    const dateStr = ts
      ? new Date(ts * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
      : null;
    const left = monthly ? `$${monthly}/mo` : null;
    const right = dateStr ? (status === 'trialing' ? `Trial ends on ${dateStr}` : `Renews on ${dateStr}`) : null;
    return [left, right].filter(Boolean).join(' • ');
  }, [isSubActive, monthly, status, subscription]);
  const showOrganizationTeamPanel =
    isSubActive && businessProfileComplete && Boolean(isPrimaryAccountHolder);
  const activeOrganization = useMemo(
    () =>
      organizations.find((organization) => organization.orgId === orgId)
      ?? organizations.find((organization) => organization.isActive)
      ?? null,
    [organizations, orgId],
  );
  const showClientSchedulePanels =
    activeOrganization?.organizationType === 'business'
    && Boolean(activeOrganization.orgId)
    && Boolean(isPrimaryAccountHolder);
  const accountHealth = dashboardSummary?.accountHealth ?? null;

  const contractedMonthlyMinutesMin = useMemo(() => {
    const value = Number(accountHealth?.contractedMonthlyMinutesMin);
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [accountHealth]);

  const contractedMonthlyMinutesMax = useMemo(() => {
    const value = Number(accountHealth?.contractedMonthlyMinutesMax);
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [accountHealth]);

  const consumedBillableMinutes = useMemo(() => {
    const value = Number(accountHealth?.consumedBillableMinutes);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }, [accountHealth]);

  const effectiveContractMaxMinutes = contractedMonthlyMinutesMax ?? contractedMonthlyMinutesMin ?? null;

  const ringProgress = useMemo(() => {
    if (!effectiveContractMaxMinutes || consumedBillableMinutes <= 0) return 0;
    return Math.max(0.04, Math.min(1, consumedBillableMinutes / effectiveContractMaxMinutes));
  }, [consumedBillableMinutes, effectiveContractMaxMinutes]);

  const utilizationRingGradient = useMemo(() => {
    const trackedOverCap = Boolean(effectiveContractMaxMinutes && consumedBillableMinutes > effectiveContractMaxMinutes);
    const accent = trackedOverCap ? 'rgba(248,113,113,.9)' : 'rgba(110,214,171,.94)';
    return `conic-gradient(${accent} 0deg, ${accent} ${ringProgress * 360}deg, rgba(255,255,255,.08) ${
      ringProgress * 360
    }deg, rgba(255,255,255,.08) 360deg)`;
  }, [consumedBillableMinutes, effectiveContractMaxMinutes, ringProgress]);

  const trackedHoursLabel = useMemo(
    () => formatHours(consumedBillableMinutes, '0h'),
    [consumedBillableMinutes],
  );

  const monthlyCapLabel = useMemo(
    () => formatHours(effectiveContractMaxMinutes, '--'),
    [effectiveContractMaxMinutes],
  );

  const projectDirectionEntries = useMemo(() => {
    const rows = dashboardSummary?.utilizationDirection?.lanes ?? [];
    const totalWeight =
      Number(dashboardSummary?.utilizationDirection?.totalProjectedMinutes) ||
      rows.reduce((sum, row) => sum + (Number(row.estimatedMinutes) || 0), 0);
    const palette = [
      {
        color: 'linear-gradient(90deg, rgba(73,215,255,.96), rgba(123,131,255,.96))',
        dotColor: '#6ce0ff',
      },
      {
        color: 'linear-gradient(90deg, rgba(121,255,188,.94), rgba(77,214,144,.98))',
        dotColor: '#67efb0',
      },
      {
        color: 'linear-gradient(90deg, rgba(255,182,123,.96), rgba(255,120,84,.96))',
        dotColor: '#ff9868',
      },
      {
        color: 'linear-gradient(90deg, rgba(188,196,214,.92), rgba(126,138,160,.96))',
        dotColor: '#cfd7e7',
      },
    ];

    const progressForMinutes = (estimatedMinutes: number) => {
      if (estimatedMinutes <= 0) return 0;
      const ratio =
        effectiveContractMaxMinutes && effectiveContractMaxMinutes > 0
          ? estimatedMinutes / effectiveContractMaxMinutes
          : totalWeight > 0
            ? estimatedMinutes / totalWeight
            : 0;
      return Math.max(0.08, Math.min(1, ratio));
    };

    return rows.map((row, index) => {
      const estimatedMinutes = Number(row.estimatedMinutes) || 0;
      const progress = progressForMinutes(estimatedMinutes);
      const dotColor = palette[index]?.dotColor ?? palette[palette.length - 1].dotColor;
      return {
        id: row.id,
        name: row.name,
        estimatedMinutes,
        share: totalWeight > 0 ? estimatedMinutes / totalWeight : 0,
        label: formatWholeHours(estimatedMinutes, '0h'),
        progress,
        color: palette[index]?.color ?? palette[palette.length - 1].color,
        dotColor,
        ringGradient: `conic-gradient(${dotColor} 0deg, ${dotColor} ${
          progress * 360
        }deg, rgba(255,255,255,.08) ${progress * 360}deg, rgba(255,255,255,.08) 360deg)`,
      };
    });
  }, [dashboardSummary, effectiveContractMaxMinutes]);

  const projectRingSlots = useMemo(
    () => Array.from({ length: 4 }, (_, index) => projectDirectionEntries[index] ?? null),
    [projectDirectionEntries],
  );
  const organizationTeamTitle = useMemo(() => {
    const rawName = String(dashboardSummary?.organizationName ?? '').trim();
    return rawName ? `${rawName} Team` : 'Organization Team';
  }, [dashboardSummary?.organizationName]);
  const organizationTeamLogoUrl = useMemo(() => {
    const raw = dashboardSummary?.organizationLogoUrl;
    return typeof raw === 'string' && raw.trim().length ? raw.trim() : null;
  }, [dashboardSummary?.organizationLogoUrl]);
  const showDashboardSkeleton = dashboardSummaryLoading && !dashboardSummary;

  const topOverviewContent = showDashboardSkeleton ? (
    <TopOverviewGrid>
      <TopWelcomeBlock>
        <WelcomeSkeleton>
          <SkeletonBlock $width="19rem" $height="3.35rem" $radius="18px" />
          <SkeletonBlock $width="12rem" $height="0.95rem" />
        </WelcomeSkeleton>
      </TopWelcomeBlock>

      <TopSnapshotBlock>
        <DashboardSkeletonCard>
          <SnapshotSkeletonLayout>
            <SnapshotSkeletonPanel>
              <SkeletonBlock $width="8.5rem" $height="0.8rem" />
              <SkeletonRing />
            </SnapshotSkeletonPanel>
            <SnapshotSkeletonPanel>
              <SkeletonBlock $width="13rem" $height="0.8rem" />
              <SkeletonBlock $width="11rem" $height="1rem" />
              <SkeletonBlock $width="10rem" $height="1rem" />
              <SkeletonBlock $width="12rem" $height="1rem" />
            </SnapshotSkeletonPanel>
          </SnapshotSkeletonLayout>
        </DashboardSkeletonCard>
      </TopSnapshotBlock>

      <TopTasksBlock>
        <DashboardSkeletonCard>
          <SkeletonBlock $width="8rem" $height="1.35rem" />
          <SkeletonBlock $width="15rem" $height="0.95rem" />
          <SkeletonBlock $width="72%" $height="0.85rem" />
          <TasksSkeletonList>
            {Array.from({ length: 3 }, (_, index) => (
              <TaskSkeletonItem key={`task-skeleton-${index}`}>
                <SkeletonBlock $width="62%" $height="1rem" />
                <SkeletonBlock $width="82%" $height="0.85rem" />
                <SkeletonBlock $width="48%" $height="0.85rem" />
              </TaskSkeletonItem>
            ))}
          </TasksSkeletonList>
        </DashboardSkeletonCard>
      </TopTasksBlock>
    </TopOverviewGrid>
  ) : (
    <TopOverviewGrid>
      <TopWelcomeBlock>
        <Welcome>
          <h2>Welcome{isPaid ? ' back' : ''}!</h2>
          <p>Here's a quick overview of your account.</p>
        </Welcome>
      </TopWelcomeBlock>

      <TopSnapshotBlock>
        <SnapshotCard>
          <SnapshotBody>
            <UtilizationPanel>
              <SnapshotKicker>Account Snapshot</SnapshotKicker>
              <UtilizationBody>
                <UtilizationRingShell $gradient={utilizationRingGradient}>
                  <UtilizationRingInner>
                    <UtilizationRingValue>
                      {effectiveContractMaxMinutes
                        ? `${trackedHoursLabel} / ${monthlyCapLabel}`
                        : trackedHoursLabel}
                    </UtilizationRingValue>
                    <UtilizationRingMeta>
                      {effectiveContractMaxMinutes ? 'Tracked / monthly cap' : 'Tracked this cycle'}
                    </UtilizationRingMeta>
                  </UtilizationRingInner>
                </UtilizationRingShell>
              </UtilizationBody>
            </UtilizationPanel>

            <DeliverySummaryCard>
              <SectionLabel>Estimated Project Utilization</SectionLabel>
              {projectDirectionEntries.length ? (
                <ProjectRingGrid>
                  {projectRingSlots.map((segment, index) =>
                    segment ? (
                      <ProjectRingSlot key={segment.id}>
                        <ProjectRingShell $gradient={segment.ringGradient}>
                          <ProjectRingInner>
                            <ProjectRingValue>{segment.label}</ProjectRingValue>
                            <ProjectRingMeta>Estimate</ProjectRingMeta>
                          </ProjectRingInner>
                        </ProjectRingShell>
                        <ProjectRingName>{segment.name}</ProjectRingName>
                      </ProjectRingSlot>
                    ) : (
                      <ProjectRingSlot key={`empty-${index}`} $empty>
                        <ProjectRingGhostShell $gradient="none">
                          <ProjectRingGhostInner />
                        </ProjectRingGhostShell>
                        <ProjectRingGhostName>&nbsp;</ProjectRingGhostName>
                      </ProjectRingSlot>
                    ),
                  )}
                </ProjectRingGrid>
              ) : (
                <ProjectRingEmpty>
                  No estimated project utilization is shaping this cycle yet.
                </ProjectRingEmpty>
              )}
            </DeliverySummaryCard>
          </SnapshotBody>
        </SnapshotCard>
      </TopSnapshotBlock>

      <TopTasksBlock>
        <OnboardingTasksPanel fillHeight />
      </TopTasksBlock>
    </TopOverviewGrid>
  );

  const activeSubscriptionContent =
    showDashboardSkeleton && isSubActive ? (
      <HeroSkeletonCard>
        <SkeletonBlock $width="12rem" $height="0.9rem" />
        <SkeletonBlock $width="18rem" $height="2rem" $radius="14px" />
        <SkeletonBlock $width="22rem" $height="0.95rem" />
      </HeroSkeletonCard>
    ) : isSubActive && activePlanId ? (
      <ActiveSubscriptionHero
        planId={activePlanId}
        planLabel={planLabel}
        subText={subText || undefined}
      />
    ) : null;

  const scheduleContent =
    showClientSchedulePanels && showDashboardSkeleton ? (
      <ScheduleSkeletonGrid>
        <ScheduleSkeletonCalendar>
          <SkeletonBlock $width="9rem" $height="0.8rem" />
          <SkeletonBlock $width="7rem" $height="1.5rem" />
          <SkeletonBlock $width="15rem" $height="0.95rem" />
          <ScheduleSkeletonCalendarGrid>
            {Array.from({ length: 35 }, (_, index) => (
              <ScheduleSkeletonDay key={`calendar-skeleton-${index}`}>
                <SkeletonBlock $width="1.2rem" $height="0.9rem" $radius="10px" />
                <SkeletonBlock $width="70%" $height="0.7rem" $radius="999px" />
              </ScheduleSkeletonDay>
            ))}
          </ScheduleSkeletonCalendarGrid>
        </ScheduleSkeletonCalendar>

        <ScheduleSkeletonRail>
          <ScheduleSkeletonRailCard>
            <SkeletonBlock $width="6rem" $height="0.8rem" />
            <SkeletonBlock $width="11rem" $height="1.5rem" />
            <SkeletonBlock $width="14rem" $height="0.95rem" />
            <SkeletonBlock $width="82%" $height="1rem" />
          </ScheduleSkeletonRailCard>
          <ScheduleSkeletonRailCard>
            <SkeletonBlock $width="8rem" $height="0.8rem" />
            <SkeletonBlock $width="6rem" $height="2.4rem" $radius="16px" />
            <SkeletonBlock $width="70%" $height="0.95rem" />
          </ScheduleSkeletonRailCard>
          <ScheduleSkeletonRailCard>
            <SkeletonBlock $width="5rem" $height="0.8rem" />
            <SkeletonBlock $width="10rem" $height="1.5rem" />
            <SkeletonBlock $width="16rem" $height="0.95rem" />
            <TasksSkeletonList>
              {Array.from({ length: 2 }, (_, index) => (
                <TaskSkeletonItem key={`event-skeleton-${index}`}>
                  <SkeletonBlock $width="55%" $height="0.9rem" />
                  <SkeletonBlock $width="72%" $height="0.85rem" />
                  <SkeletonBlock $width="48%" $height="0.85rem" />
                </TaskSkeletonItem>
              ))}
            </TasksSkeletonList>
          </ScheduleSkeletonRailCard>
        </ScheduleSkeletonRail>
      </ScheduleSkeletonGrid>
    ) : showClientSchedulePanels ? (
      <ClientScheduleOverview
        orgId={dashboardSummary?.orgId ?? orgId ?? null}
        organizationName={dashboardSummary?.organizationName ?? null}
        organizationLogoUrl={dashboardSummary?.organizationLogoUrl ?? null}
        meetingSummary={dashboardSummary?.meetingSummary ?? null}
        meetingSummaryLoading={dashboardSummaryLoading}
      />
    ) : null;

   type UiPriority = 'urgent' | 'high' | 'medium' | 'low';
 function mapPriorityToApi(p: UiPriority): 'P0' | 'P1' | 'P2' | 'P3' {
   return p === 'urgent' ? 'P0' : p === 'high' ? 'P1' : p === 'low' ? 'P3' : 'P2';
 }



  // Called by NotificationsCard when user clicks "Open"
 const openFromNotif = useCallback(async (id: string) => {
   const ticketId = String(id ?? '').trim();
   if (!ticketId) return;
   let canOpen = false;
   try {
     const { data } = await api.get(`/tickets/${encodeURIComponent(ticketId)}`);
     if (data?.id) {
       dispatch(ticketUpserted(data as any));
       canOpen = true;
     }
   } catch (error) {
     console.warn('[ClientHomePage] Failed to fetch ticket for notification open', error);
   }
   if (canOpen) {
     setEditingId(ticketId);
   }
 }, [api, dispatch]);

 // When the ticket appears in the store, actually open the editor
 useEffect(() => {
   // Open once when the ticket data for the chosen id is available
   if (editingId && editing) setEditorOpen(true);
 }, [editingId, editing]);

 async function handleUpdate(id: string, patch: Partial<{
   title: string;
   description: string;
   projectId: string;
   priority: UiPriority;
   assignedAgentId: string | null;
   offeringId: string | null;
   offeringSubcategoryId: string | null;
   kbaseProcessId: string | null;
   status: 'open' | 'in_progress' | 'blocked' | 'resolved' | 'closed';
   subtasks: any[];
   participants: string[];
 }>) {
   // Dedicated endpoints
   if (patch.assignedAgentId !== undefined) {
     await dispatch(assignTicket({ id, agentId: patch.assignedAgentId })).unwrap();
   }
   if (patch.status) {
     await dispatch(updateTicketStatus({ id, status: patch.status })).unwrap();
   }
   // Generic fields
   const body: any = {};
   if (patch.title !== undefined) body.title = patch.title;
   if (patch.description !== undefined) body.description = patch.description;
   if (patch.projectId !== undefined) body.projectId = patch.projectId || null;
   if (patch.priority !== undefined) body.priority = mapPriorityToApi(patch.priority);
   if (patch.offeringId !== undefined) body.offeringId = patch.offeringId || null;
   if (patch.offeringSubcategoryId !== undefined) {
     body.offeringSubcategoryId = patch.offeringSubcategoryId || null;
   }
   if (patch.kbaseProcessId !== undefined) body.kbaseProcessId = patch.kbaseProcessId || null;
   if (patch.subtasks !== undefined) body.subtasks = patch.subtasks;
   if (patch.participants !== undefined) body.participants = patch.participants;
   if (Object.keys(body).length) await api.patch(`/tickets/${id}`, body);
   // Refresh list so other widgets stay in sync
   await dispatch(fetchTickets({ orderBy: 'plannedEndAt', direction: 'asc', limit: 200 }));
 }

 const handleDeleteTicket = useCallback(async (id: string) => {
   await api.delete(`/tickets/${id}`);
   setEditorOpen(false);
   await dispatch(fetchTickets({ orderBy: 'plannedEndAt', direction: 'asc', limit: 200 }));
 }, [api, dispatch]);


 const editorTicket = useMemo(() => {
   return buildTicketEditorTicket(editing, {
     onDelete: handleDeleteTicket,
   });
 }, [editing, handleDeleteTicket]);

const handleCloseEditor = useCallback(() => {
   setEditorOpen(false);
   setEditingId(null);        // prevent the auto-open effect from re-opening
}, []);

  // Later you can pass real props (nextDue, planName, ticket counts, data series, etc).
  return (
    <PageWrap>
      {isMobileTasksMode ? (
        <>
          <MobileTasksEdgeTab
            type="button"
            aria-label="Open tasks drawer"
            onClick={openMobileTasks}
            style={{ opacity: mobileTasksOpen ? 0 : 1, pointerEvents: mobileTasksOpen ? 'none' : 'auto' }}
          >
            <ListTodo size={14} />
            Tasks
          </MobileTasksEdgeTab>

          <MobileTasksOverlay
            type="button"
            aria-label="Close tasks drawer"
            $open={mobileTasksOpen}
            onClick={closeMobileTasks}
          />

          <MobileTasksDrawer $open={mobileTasksOpen} aria-label="Tasks drawer">
            <MobileTasksDrawerHeader>
              <MobileTasksDrawerHeaderText>
                <MobileTasksDrawerEyebrow>Dashboard</MobileTasksDrawerEyebrow>
                <MobileTasksDrawerTitle>All Tasks</MobileTasksDrawerTitle>
                <MobileTasksDrawerSubtitle>
                  Review your active and completed tasks in one place.
                </MobileTasksDrawerSubtitle>
              </MobileTasksDrawerHeaderText>
              <MobileTasksDrawerClose
                type="button"
                aria-label="Close tasks drawer"
                onClick={closeMobileTasks}
              >
                <X size={16} />
              </MobileTasksDrawerClose>
            </MobileTasksDrawerHeader>
            <MobileTasksDrawerBody>
              <MobileTasksPanel fillHeight />
            </MobileTasksDrawerBody>
          </MobileTasksDrawer>
        </>
      ) : null}

      <Grid>
        {/* Row 1: Welcome (8)  Billing (4) */}
        <Span12>{topOverviewContent}</Span12>

        {activeSubscriptionContent ? <Span12>{activeSubscriptionContent}</Span12> : null}
        {scheduleContent ? <Span12 id="client-schedule-overview">{scheduleContent}</Span12> : null}
        {showOrganizationTeamPanel ? (
          <Span6Right>
            <FixedHeightPanel>
              <TeamMembersFill
                onInviteClick={openInvite}
                title={organizationTeamTitle}
                titleLogoUrl={organizationTeamLogoUrl}
                readOnly
              />
            </FixedHeightPanel>
          </Span6Right>
        ) : null}

        {/* Row 3: Activity (6)  SLA (6) */}
        {/* <Span6>
          <Card>
            <div style={{ fontWeight: 800, color: '#E8EAED', marginBottom: 8 }}>Activity</div>
            <div style={{ color: '#9AA4B2' }}>Recent activity will appear here.</div>
          </Card>
        </Span6> */}
        {/* <Span6><SLAChartCard /></Span6> */}
      </Grid>
      <InviteTeammateModal open={inviteOpen} onClose={closeInvite} orgId={orgId || ''} />
 <TicketEditorDrawer
   open={Boolean(editorOpen && editorTicket)}
   mode={editorMode}
   onClose={handleCloseEditor}
   ticket={editorTicket}
   agents={[]}
   // These are required by the component’s API; create is unused here.
   onCreate={async () => { throw new Error('Create not supported from home'); }}
   onUpdate={handleUpdate}
 />


    </PageWrap>
  );
}

